'use client';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Globe, Briefcase, Landmark, Radio, BarChart3 } from 'lucide-react';
import { useLanguage } from '@/components/LanguageProvider';

function GachaCard({ children, className, colSpan }: { children: React.ReactNode, className?: string, colSpan?: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "0px" }} transition={{ duration: 0.5 }} className={`${colSpan || ''} relative rounded-[20px] md:rounded-[24px] will-change-transform ${className}`}>
      {children}
    </motion.div>
  );
}

export default function AboutSection() {
  const { t, locale } = useLanguage();
  const tl = t.landing;

  return (
    <section id="about" className="py-20 md:py-32 w-full relative z-20">
      <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12 md:mb-16 will-change-transform text-center md:text-left flex flex-col items-center md:items-start">
        <h2 className="uppercase text-white leading-tight mb-4">
          <span id="about-heading" className="font-mc text-3xl sm:text-4xl md:text-5xl block tracking-normal mb-1">
            {tl.about.heading1}
          </span>
          <span className="text-3xl sm:text-4xl md:text-5xl text-[#667fff] font-extrabold tracking-tight block">
            {tl.about.heading2}
          </span>
        </h2>
        <p className="text-[#aaaaaa] max-w-2xl text-base md:text-lg font-medium leading-relaxed mt-2">
          {tl.about.desc}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[280px] md:auto-rows-[320px]">
        <GachaCard colSpan="md:col-span-7" className="bg-[#161616] group border border-white/5 overflow-hidden">
            <Image src={locale === 'en' ? "/landing2.0/survive1.png" : "/landing2.0/survive1.png"} alt="Survival" fill sizes="(max-width: 768px) 100vw, 60vw" className="object-cover opacity-60 group-hover:scale-[1.03] transition-transform duration-700 ease-out will-change-transform" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 md:p-8 w-full relative z-10 flex flex-col h-full justify-end">
            <div className="flex items-center gap-2 text-[#667fff] mb-3">
                <Globe size={18} />
                <span className="font-mc text-[13px] uppercase tracking-wider mt-0.5">{tl.about.societyTag}</span>
            </div>
            <h3 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-2">{tl.about.societyTitle}</h3>
            <p className="text-[#aaaaaa] text-sm md:text-base max-w-md leading-relaxed">{tl.about.societyDesc}</p>
            </div>
        </GachaCard>

        <GachaCard colSpan="md:col-span-5" className="bg-[#1a1a1a] border border-white/5 group overflow-hidden">
            <div className="p-6 md:p-8 h-full flex flex-col justify-between relative z-10">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-4"><Briefcase size={22} /></div>
                <div>
                    <h3 className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-2">{tl.about.econTitle}</h3>
                    <p className="text-[#aaaaaa] text-sm md:text-base font-medium leading-relaxed">{tl.about.econDesc}</p>
                </div>
            </div>
            <div className="absolute bottom-4 right-4 text-white/5 transition-opacity duration-500">
            <BarChart3 size={120} className="md:w-[140px] md:h-[140px]" />
            </div>
        </GachaCard>

        <GachaCard colSpan="md:col-span-5" className="bg-[#1a1a1a] border border-white/5 group overflow-hidden">
            <div className="p-6 md:p-8 h-full flex flex-col justify-between relative z-10">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white mb-4"><Landmark size={22} /></div>
                <div>
                <h3 className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white mb-2">{tl.about.lawTitle}</h3>
                <p className="text-[#aaaaaa] text-sm md:text-base font-medium leading-relaxed">{tl.about.lawDesc}</p>
                </div>
            </div>
        </GachaCard>

        <GachaCard colSpan="md:col-span-7" className="bg-[#161616] border border-white/5 overflow-hidden group">
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-center md:justify-between h-full p-6 md:px-10 gap-6 md:gap-8">
            <div className="text-center md:text-left flex-1 flex flex-col items-center md:items-start">
                <div className="font-mc inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white text-[12px] uppercase mb-4 tracking-wider cursor-default">
                    <Radio size={14} className="text-[#667fff] -mt-[1px]" /> Plasmo Voice
                </div>
                <h3 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-3">{tl.about.voiceTitle}</h3>
                <p className="text-[#aaaaaa] font-medium text-sm md:text-base leading-relaxed max-w-sm">{tl.about.voiceDesc}</p>
            </div>
            <div className="flex gap-1.5 items-end h-20 md:h-24 opacity-60">
                {[...Array(12)].map((_, i) => (
                    <motion.div key={i} animate={{ height: [15, Math.random() * 60 + 15, 15] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut", delay: i * 0.05 }} className="w-1.5 md:w-2 bg-[#667fff] rounded-full will-change-transform" />
                ))}
            </div>
        </div>
        </GachaCard>
      </div>
    </section>
  );
}